import React, { FC } from "react";
import { Col, Row } from "reactstrap";
import { useFormikContext } from "formik";

import BasicSelectOption from "components/common/Form/SelectOption/BasicSelectOption";
import { TextArea } from "components/common/Form/TextArea";
import { TextInput } from "components/common/Form/TextInput/TextInput";
import {
  useGetAllCitiesByCountyId,
  useGetAllCountiesByProvinceId,
  useGetAllProvinces,
  useGetAllRuralsByCountyId,
  useGetAllVillagesByCountyId,
} from "core/services/api/location.api";
import { useOptionUpdater } from "core/hooks/use-option-updater";
import { TCartFormInitialValues } from "core/models/order.model";

const AddressForm: FC = () => {
  const { values, setFieldValue } = useFormikContext<TCartFormInitialValues>();

  //------------------------------------------------------------- Apis --------------------------------------------
  const getAllProvinces = useGetAllProvinces();
  const getAllCountiesByProvinceId = useGetAllCountiesByProvinceId(
    values.province?.value ?? 0,
  );
  const getAllCitiesByCountyId = useGetAllCitiesByCountyId(
    values.county?.value ?? 0,
  );
  const getAllRuralsByCountyId = useGetAllRuralsByCountyId(
    values.county?.value ?? 0,
  );
  const getAllVillagesByCountyId = useGetAllVillagesByCountyId(
    values.county?.value ?? 0,
  );

  //------------------------------------------------------------- Hooks --------------------------------------------

  const { options: provinceOptions } = useOptionUpdater(
    getAllProvinces,
    "id",
    "title",
  );
  const { options: countyOptions, setOptions: setCountyOptions } =
    useOptionUpdater(getAllCountiesByProvinceId, "id", "title");

  const { options: ruralOptions, setOptions: setRuralOptions } =
    useOptionUpdater(getAllRuralsByCountyId, "id", "title");

  const { options: cityOptions, setOptions: setCityOptions } = useOptionUpdater(
    getAllCitiesByCountyId,
    "id",
    "title",
  );

  const { options: villageOptions, setOptions: setVillageOptions } =
    useOptionUpdater(getAllVillagesByCountyId, "id", "title");

  //------------------------------------------------------------- Function --------------------------------------------

  const onProvinceChange = (option: any) => {
    setFieldValue("province", option);
    setFieldValue("county", null);

    if (option?.value !== values.province?.value) {
      setCountyOptions([]);
    }
  };

  const onCountyChange = (option: any) => {
    setFieldValue("county", option);
    setFieldValue("city", null);
    setFieldValue("rural", null);
    setFieldValue("village", null);

    if (option?.value !== values.county?.value) {
      setCityOptions([]);
      setRuralOptions([]);
      setVillageOptions([]);
    }
  };

  const onCityChange = (option: any) => {
    setFieldValue("city", option);
    setFieldValue("rural", { value: 0, label: "شهر انتخاب شده است" });
  };

  const onRuralChange = (option: any) => {
    setFieldValue("rural", option);
    setFieldValue("city", { value: 0, label: "روستا انتخاب شده است" });
  };

  //------------------------------------------------------------- Render --------------------------------------------

  return (
    <>
      <Col xs="12">
        <BasicSelectOption
          name="province"
          lableText="استان"
          placeHolder="استان مورد نظر را انتخاب کنید"
          data={provinceOptions}
          significant
          isLoading={getAllProvinces.isLoading}
          onChange={onProvinceChange}
        />
      </Col>
      <Col xs="12">
        <BasicSelectOption
          name="county"
          lableText="شهرستان"
          placeHolder="شهرستان مورد نظر را انتخاب کنید"
          data={countyOptions}
          significant
          isLoading={getAllCountiesByProvinceId.isLoading}
          onChange={onCountyChange}
        />
      </Col>
      <Col xs="12">
        <BasicSelectOption
          name="city"
          lableText="شهر"
          placeHolder="شهر مورد نظر را انتخاب کنید"
          data={cityOptions}
          significant
          isLoading={getAllCitiesByCountyId.isLoading}
          onChange={onCityChange}
        />
      </Col>
      <Col xs="12">
        <BasicSelectOption
          name="rural"
          lableText="دهستان"
          placeHolder="دهستان مورد نظر را انتخاب کنید"
          data={ruralOptions}
          significant
          isLoading={getAllRuralsByCountyId.isLoading}
          onChange={onRuralChange}
        />
      </Col>
      {values.rural && values.rural?.value !== 0 && (
        <Col xs="12">
          <BasicSelectOption
            name="village"
            lableText="روستا"
            placeHolder="روستا مورد نظر را انتخاب کنید"
            data={villageOptions}
            isLoading={getAllVillagesByCountyId.isLoading}
            significant
          />
        </Col>
      )}
      <Col xs="12">
        <TextInput
          lableText="کد پستی"
          name="postalCode"
          placeholder="کد پستی را وارد کنید"
          significant
        />
      </Col>
      <Col xs="12">
        <TextArea
          lableText="آدرس"
          name="address"
          placeholder="آدرس را وارد کنید"
          significant
        />
      </Col>
    </>
  );
};

export { AddressForm };
